/**
 * Custom WebView with autoHeight feature
 *
 * @prop source: Same as WebView
 * @prop autoHeight: true|false
 * @prop defaultHeight: 100
 * @prop maxHeight: 100
 * @prop width: device Width
 * @prop ...props
 *
 * @author Elton Jain
 * @version v1.0.2
 * from github.com/scazzy/react-native-webview-autoheight
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import { Animated, Linking, Platform, Text, View } from "react-native";
import { WebView } from "react-native-webview";

import { Button } from "native-base";
import { withTranslation } from "react-i18next";

import AppStyles from "../../theme/styles";
import DebugLogger from "../../utils/DebugLogger";
import * as Helper from "../../utils/Helper";
import UIDs from "../../UIDs";

import ViewPropTypes from "deprecated-react-native-prop-types";

const injectedScriptFn = (scalesPageToFit, metaContent) =>
    `
    ;(function() {
        var resizeTimeout;
        function resizeThrottler() {
        // ignore resize events as long as an actualResizeHandler execution is in the queue
        if ( !resizeTimeout ) {
          resizeTimeout = setTimeout(function() {
            resizeTimeout = null;
            waitForBridge();
         
           // The actualResizeHandler will execute at a rate of 15fps
           },  66);
        }
        }
        //var _autoHeightHashIndex = 0;
        var usingScale = 1;
        var processing = false;
        var scaling = false;
        var zoomedin = false;
        var lastHeight = 0;
        var heightTheSameTimes = 0;
        var maxHeightTheSameTimes = 5;
        var forceRefreshDelay = 1000;
        var forceRefreshTimeout;
        
        function setDeviceWidthMeta(content){
            var meta = document.createElement("meta");
            meta.setAttribute("name", "viewport");
            meta.setAttribute("content", content);
            document.getElementsByTagName("head")[0].appendChild(meta);
        }
        
        function updateSize(){
            try {
                usingScale = ${scalesPageToFit ? "screen.width / window.innerWidth" : "1"};
                clearTimeout(forceRefreshTimeout);
            }catch(e){}        //support this.props.scalesPageToFit
            // var height = Math.max(document.documentElement.clientHeight, document.documentElement.scrollHeight, document.body.clientHeight, document.body.scrollHeight) * usingScale;
            var height = Math.floor((wrapper.offsetHeight|| document.documentElement.offsetHeight) * usingScale);
            //use max for all ava heights will be ok
            window.ReactNativeWebView.postMessage(height);
            
            //window.location.hash = ++_autoHeightHashIndex;
            if (lastHeight !== height) {
              heightTheSameTimes = 1;
            } else {
              heightTheSameTimes++;
            }
            lastHeight = height;
            if (heightTheSameTimes <= maxHeightTheSameTimes) {
              forceRefreshTimeout = setTimeout(
                updateSize,
                heightTheSameTimes * forceRefreshDelay
              );
            }
        }
        
        function waitForBridge() {
            
            if (!window.hasOwnProperty("ReactNativeWebView") || !window.ReactNativeWebView.hasOwnProperty("postMessage")) {
                setTimeout(waitForBridge, 200);
            } else {
                updateSize();
            }
        }

        try {
            //change body style
            var styleElement = document.createElement("style");
            styleElement.innerHTML = "*{box-sizing: border-box;}p{margin-top: 4px;margin-bottom: 4px}body{margin:0;padding:0;}#iml-auto-wrapper{padding-top:4px;padding-bottom:4px}";
            document.head.appendChild(styleElement);
            
            //use a wrapper to wrap all elements in html to avoid the height blow up issue
            var wrapper;
            if (!document.getElementById("iml-auto-wrapper")) {
                wrapper = document.createElement("div");
                wrapper.id = "iml-auto-wrapper";
                while (document.body.firstChild instanceof Node) {
                  wrapper.appendChild(document.body.firstChild);
                }
                document.body.appendChild(wrapper);
            }
            
            if ("${metaContent}" !== ""){
                setDeviceWidthMeta("${metaContent}");
            }
        }catch(e){}
        window.addEventListener("load", waitForBridge);
        window.addEventListener("resize", resizeThrottler, false);
        waitForBridge();
    })();
`;

const uid = UIDs.components.ui.autoHeightWebView;

class AutoHeightWebView extends Component {
    state = {
        webViewHeight: 0,
    };

    static defaultProps = {
        autoHeight: true,
        maxHeight: -1,
        scalesPageToFit: true, //the webview default scalesPageToFit is true, but on android, set to true may cause the webview html resize event keep fires -> height kep growing...
        metaContent: "width=device-width, initial-scale=1", //for mobile this is good enough
        enableAnimation: true,
        animationDuration: 555,
    };

    static propTypes = {
        autoHeight: PropTypes.bool,
        defaultHeight: PropTypes.number,
        maxHeight: PropTypes.number,
        containerStyle: ViewPropTypes.style,
        scalesPageToFit: PropTypes.bool,
        metaContent: PropTypes.string,
        enableAnimation: PropTypes.bool,
        // only works on enable animation
        animationDuration: PropTypes.number,
    };

    constructor(props) {
        super(props);
        this.state = {
            webViewHeight: this.props.defaultHeight,
            height: this.props.defaultHeight,
        };

        this._onMessage = this._onMessage.bind(this);
        this.opacityAnimatedValue = new Animated.Value(0);
    }

    _onMessage(e) {
        if (e.nativeEvent.data && Helper.isNumeric(e.nativeEvent.data)) {
            this.opacityAnimatedValue = new Animated.Value(0);
            const height = parseInt(e.nativeEvent.data);
            DebugLogger.log(`WEBVIEW HEIGHT: ${height}`);
            if (height !== this.state.webViewHeight || this.getHeight() !== this.state.height) {
                this.setState(
                    {
                        webViewHeight: height,
                        height: this.getHeight(),
                    },
                    () => {
                        if (this.props.enableAnimation) {
                            Animated.timing(this.opacityAnimatedValue, {
                                toValue: 1,
                                duration: this.props.animationDuration,
                            }).start();
                        }
                    }
                );
            }
        }
    }

    render() {
        const scalesPageToFit = Platform.OS === "ios" ? undefined : this.props.scalesPageToFit; //scalesPageToFit not support in ios
        const injectedScript = injectedScriptFn(scalesPageToFit, this.props.metaContent);
        // const _w = this.props.width || Dimensions.get("window").width;
        if (/*this.props.enableAnimation*/false) {
            // NOTE: react-native的Animated.View有bug, 手机上会导致WebView显示空白?
            return (
                <Animated.View style={[this.props.containerStyle, { height: this.state.height, opacity: this.props.enableAnimation ? this.opacityAnimatedValue : 1 }]}>
                    <WebView
                        ref={ref => (this.webView = ref)}
                        injectedJavaScript={injectedScript}
                        scrollEnabled={this.props.scrollEnabled || this.state.webViewHeight > this.state.height}
                        onMessage={this._onMessage}
                        javaScriptEnabled={true}
                        automaticallyAdjustContentInsets={true}
                        domStorageEnabled={true}
                        startInLoadingState={true}
                        onError={this.renderError}
                        scalesPageToFit={scalesPageToFit}
                        renderError={this.renderError}
                        //renderLoading={this._renderWebViewLoading}
                        source={this.props.source}
                        style={[this.props.style, { height: this.state.height, flex: 0, backgroundColor: "transparent" }]}
                        originWhitelist={["*"]}
                    />
                </Animated.View>
            );
        }
        return (
            <View style={[this.props.containerStyle, { height: this.state.height }]}>
                <WebView
                    ref={ref => (this.webView = ref)}
                    injectedJavaScript={injectedScript}
                    scrollEnabled={this.props.scrollEnabled || this.state.webViewHeight > this.state.height}
                    onMessage={this._onMessage}
                    javaScriptEnabled={true}
                    automaticallyAdjustContentInsets={true}
                    domStorageEnabled={true}
                    startInLoadingState={true}
                    onError={this.renderError}
                    scalesPageToFit={scalesPageToFit}
                    renderError={this.renderError}
                    //renderLoading={this._renderWebViewLoading}
                    source={this.props.source}
                    style={[this.props.style, { height: this.state.height, flex: 0, backgroundColor: "transparent" }]}
                    originWhitelist={["*"]}
                />
            </View>
        );
    }

    renderError = () => {
        const self = this;
        return (
            <View style={[AppStyles.paddingBottomSml]}>
                <View style={[AppStyles.centerAligned, AppStyles.paddingVertical]}>
                    <Text lg muted>
                        {this.props.t("OpenLinkErrorNote")}
                    </Text>
                </View>
                <View style={[AppStyles.row, AppStyles.centerAligned]}>
                    <Button
                        primary
                        small
                        onPress={() => {
                            Linking.openURL(self.props.source.uri).catch(err => DebugLogger.log("An error occurred", err));
                        }}>
                        <Text style={{ color: "#ffffff" }}>{this.props.t("OpenInBrowser")}</Text>
                    </Button>
                </View>
            </View>
        );
    };

    reload() {
        this.webView.reload();
    }

    onBack = () => {
        this.webView.goBack();
    };

    onForward = () => {
        this.webView.goForward();
    };

    getHeight = () => {
        let autoHeight = this.state.webViewHeight;
        if (this.props.maxHeight > 0) {
            autoHeight = this.state.webViewHeight > this.props.maxHeight ? this.props.maxHeight : this.state.webViewHeight;
        }
        DebugLogger.log(`WEBVIEW REAL HEIGHT: ${this.props.autoHeight ? autoHeight : this.props.defaultHeight}`);
        return this.props.autoHeight ? autoHeight : this.props.defaultHeight;
    };

    _renderWebViewLoading = () => {
        return (
            <View style={[AppStyles.flex1, AppStyles.centerAligned, AppStyles.widthNull]}>
                <Text style={{ ...AppStyles.baseText }}>{this.props.t("loading")}...</Text>
            </View>
        );
    };
}

export default withTranslation(uid, { withRef: true })(AutoHeightWebView);
